import { computed, onMounted, onUnmounted, ref } from 'vue';
import { readStoredNumber } from '@/utils/browser-storage.ts';

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const isOnline = ref(typeof navigator === 'undefined' ? true : navigator.onLine);
const installPrompt = ref<InstallPromptEvent>();
const waitingWorker = ref<ServiceWorker>();
const contentSyncStorageOptions = () => ({ key: 'vennevent:last-content-sync', storage: () => window.localStorage, maxBytes: 32 });
const lastContentSync = ref<number>(readStoredNumber(contentSyncStorageOptions()) ?? 0);
let initialized = false;

export function initializePwa() {
  if (initialized || !('serviceWorker' in navigator)) return;
  initialized = true;

  const online = () => (isOnline.value = true);
  const offline = () => (isOnline.value = false);
  const contentSync = () => (lastContentSync.value = readStoredNumber(contentSyncStorageOptions()) ?? Date.now());
  const beforeInstall = (event: Event) => {
    event.preventDefault();
    installPrompt.value = event as InstallPromptEvent;
  };

  onMounted(() => {
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    window.addEventListener('beforeinstallprompt', beforeInstall);
    window.addEventListener('vennevent-content-sync', contentSync);

    void navigator.serviceWorker.register('/service-worker.js', { scope: '/' }).then((registration) => {
      if (registration.waiting) waitingWorker.value = registration.waiting;
      registration.addEventListener('updatefound', () => {
        const worker = registration.installing;
        worker?.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) waitingWorker.value = worker;
        });
      });
    });
  });

  onUnmounted(() => {
    window.removeEventListener('online', online);
    window.removeEventListener('offline', offline);
    window.removeEventListener('beforeinstallprompt', beforeInstall);
    window.removeEventListener('vennevent-content-sync', contentSync);
  });
}

export function usePwa() {
  function dismissInstall() {
    installPrompt.value = undefined;
  }

  async function install() {
    const prompt = installPrompt.value;
    if (!prompt) return;
    await prompt.prompt();
    await prompt.userChoice;
    installPrompt.value = undefined;
  }

  function applyUpdate() {
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
    waitingWorker.value?.postMessage({ type: 'SKIP_WAITING' });
    waitingWorker.value = undefined;
  }

  return {
    applyUpdate,
    canInstall: computed(() => !!installPrompt.value),
    dismissInstall,
    install,
    isOnline,
    lastContentSync,
    updateAvailable: computed(() => !!waitingWorker.value),
  };
}
